import { EnumUserCurrentTab } from './../../constants/enums';
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_API_ERROR } from "../../constants/commonConstants";
import { iApiError, iApiSuccess } from "../../types/commonTypes";
import { iUserDetails } from "../../types/userType";
import type { ISendbirdSessionPayload } from "../../types/sendbirdTypes";

import {
  loginUserThunk,
  logoutUserThunk,
  closeChanelThunk,
  fetchTeamMemberSendbirdSessionThunk,
  fetchLegacyVendorOwnerSendbirdSessionThunk,
  changePasswordThunk,
} from "./userThunk";

import {
  getAccessToken,
  getRefershToken,
  removeAuthTokens,
  setAuthTokens,
} from "../../helpers/authHelpers";

export interface iUserStoreState {
  userDetails: iUserDetails | null;
  isLoading: boolean;
  isTeamMemberSendbirdTokenLoading: boolean;
  isLegacySendbirdTokenLoading: boolean;
  apiError: iApiError | null;
  teamMemberSendbirdApiError: iApiError | null;
  legacySendbirdApiError: iApiError | null;
  isLoggedIn: boolean;
  chatSessionToken: string;
  ChatSessionExpireTime: number | null;
  sendbirdApplicationId: string;
  teamMemberSendbirdSession: ISendbirdSessionPayload | null;
  legacyVendorOwnerSendbirdSession: ISendbirdSessionPayload | null;
  apiSuccess: iApiSuccess | null;
  userCurrentTab: EnumUserCurrentTab,
  deviceToken: string | null,
  /**
   * The temporary password the vendor just signed in with, sent back as
   * `currentPassword` so the change-password screen need not ask for it.
   * Memory only: the persist whitelist leaves it out, so it never reaches
   * localStorage and a reload drops it.
   */
  temporaryPassword: string | null,
}

const initialState: iUserStoreState = {
  userDetails: null,
  isLoading: false,
  isTeamMemberSendbirdTokenLoading: false,
  isLegacySendbirdTokenLoading: false,
  apiError: null,
  chatSessionToken: "",
  ChatSessionExpireTime: null,
  sendbirdApplicationId: "",
  teamMemberSendbirdSession: null,
  legacyVendorOwnerSendbirdSession: null,
  isLoggedIn: !!getRefershToken() || !!getAccessToken(),
  apiSuccess: null,
  teamMemberSendbirdApiError: null,
  legacySendbirdApiError: null,
  userCurrentTab: EnumUserCurrentTab.Details,
  deviceToken: null,
  temporaryPassword: null,
};

const applyActiveSendbirdSession = (state: iUserStoreState, session: ISendbirdSessionPayload) => {
  state.chatSessionToken = session.accessToken;
  state.ChatSessionExpireTime = session.expiresAt;
  if (session.applicationId) {
    state.sendbirdApplicationId = session.applicationId;
  }
};

const clearSendbirdSessions = (state: iUserStoreState) => {
  state.chatSessionToken = "";
  state.ChatSessionExpireTime = null;
  state.sendbirdApplicationId = "";
  state.teamMemberSendbirdSession = null;
  state.legacyVendorOwnerSendbirdSession = null;
  state.teamMemberSendbirdApiError = null;
  state.legacySendbirdApiError = null;
};

const userSlice = createSlice({
  name: "user",
  initialState: initialState,

  reducers: {
    logoutUser(state) {
      removeAuthTokens();
      state.userDetails = initialState.userDetails;
      state.isLoading = false;
      state.isTeamMemberSendbirdTokenLoading = false;
      state.isLegacySendbirdTokenLoading = false;
      clearSendbirdSessions(state);
      state.apiError = null;
      state.isLoggedIn = false;
      state.temporaryPassword = null;
    },
    resetApiError(state) {
      state.apiError = null;
      state.apiSuccess = null;
    },
    setUserCurrentTab(state, action: PayloadAction<EnumUserCurrentTab>) {
      state.userCurrentTab = action.payload;
    },
    setDeviceTokenGlobally(state, action: PayloadAction<string | null>) {
      state.deviceToken = action.payload;
    },
    requirePasswordChange(state) {
      if (state.userDetails) {
        state.userDetails.mustChangePassword = true;
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(loginUserThunk.fulfilled, (state, action) => {
      state.userDetails = action.payload.data.user;
      state.isLoading = false;
      state.isLoggedIn = true;
      state.temporaryPassword = action.payload.data.user.mustChangePassword
        ? action.meta.arg.password
        : null;
      setAuthTokens(action.payload.data.tokens);
    });
    builder.addCase(loginUserThunk.pending, (state) => {
      state.apiError = null;
      state.isLoading = true;
    });
    builder.addCase(loginUserThunk.rejected, (state, action) => {
      state.apiError = action.payload ?? DEFAULT_API_ERROR;
      state.isLoading = false;
    });

    builder.addCase(logoutUserThunk.fulfilled, (state) => {
      state.userDetails = null;
      state.isLoading = false;
      state.isTeamMemberSendbirdTokenLoading = false;
      state.isLegacySendbirdTokenLoading = false;
      clearSendbirdSessions(state);
      state.isLoggedIn = false;
      state.temporaryPassword = null;
    });
    builder.addCase(logoutUserThunk.pending, (state) => {
      state.apiError = null;
      state.isLoading = true;
    });
    builder.addCase(logoutUserThunk.rejected, (state, action) => {
      state.apiError = action.payload ?? DEFAULT_API_ERROR;
      state.isLoading = false;
    });

    builder.addCase(fetchTeamMemberSendbirdSessionThunk.pending, (state) => {
      state.isTeamMemberSendbirdTokenLoading = true;
      state.teamMemberSendbirdApiError = null;
    });
    builder.addCase(fetchTeamMemberSendbirdSessionThunk.fulfilled, (state, action) => {
      state.teamMemberSendbirdSession = action.payload;
      applyActiveSendbirdSession(state, action.payload);
      state.isTeamMemberSendbirdTokenLoading = false;
      state.teamMemberSendbirdApiError = null;
    });
    builder.addCase(fetchTeamMemberSendbirdSessionThunk.rejected, (state, action) => {
      state.isTeamMemberSendbirdTokenLoading = false;
      state.teamMemberSendbirdApiError = action.payload ?? DEFAULT_API_ERROR;
    });

    builder.addCase(fetchLegacyVendorOwnerSendbirdSessionThunk.pending, (state) => {
      state.isLegacySendbirdTokenLoading = true;
      state.legacySendbirdApiError = null;
    });
    builder.addCase(fetchLegacyVendorOwnerSendbirdSessionThunk.fulfilled, (state, action) => {
      state.legacyVendorOwnerSendbirdSession = action.payload;
      state.isLegacySendbirdTokenLoading = false;
      state.legacySendbirdApiError = null;
    });
    builder.addCase(fetchLegacyVendorOwnerSendbirdSessionThunk.rejected, (state, action) => {
      state.isLegacySendbirdTokenLoading = false;
      state.legacySendbirdApiError = action.payload ?? DEFAULT_API_ERROR;
    });

    // Every earlier refresh token was revoked by the change, so the session is
    // replaced with the one it returns. A null `data` is handled by the screen,
    // which logs the vendor out to sign in with the new password.
    builder.addCase(changePasswordThunk.fulfilled, (state, action) => {
      state.temporaryPassword = null;
      const session = action.payload.data;
      if (!session) return;
      setAuthTokens(session.tokens);
      state.userDetails = { ...session.user, mustChangePassword: false };
    });

    builder.addCase(closeChanelThunk.fulfilled, (state, action) => {
      state.apiSuccess = action.payload;
      state.isLoading = false;
    });
    builder.addCase(closeChanelThunk.pending, (state) => {
      state.apiError = null;
      state.isLoading = true;
      state.apiSuccess = null;
    });
    builder.addCase(closeChanelThunk.rejected, (state, action) => {
      state.apiError = action.payload ?? DEFAULT_API_ERROR;
      state.isLoading = false;
    });
  },
});

export const {
  resetApiError,
  logoutUser,
  setUserCurrentTab,
  setDeviceTokenGlobally,
  requirePasswordChange,
} = userSlice.actions;
export default userSlice.reducer;
